export interface CategoryTile {
  value: string;
  label: string;
  subtitle: string;
  icon: string;
  accent: string;
  accentDark: string;
}

export const CATEGORY_TILES: CategoryTile[] = [
  {
    value: "ENVIRONMENT",
    label: "Environment",
    subtitle: "Conservation, climate, sustainability",
    icon: "🌿",
    accent: "#DCFCE7",
    accentDark: "#15803D",
  },
  {
    value: "ARTS",
    label: "Arts & culture",
    subtitle: "Performing arts, heritage, humanities",
    icon: "🎭",
    accent: "#FCE7F3",
    accentDark: "#BE185D",
  },
  {
    value: "EDUCATION",
    label: "Education",
    subtitle: "Schools, scholarships, training",
    icon: "🎓",
    accent: "#DBEAFE",
    accentDark: "#1D4ED8",
  },
  {
    value: "HEALTH",
    label: "Health",
    subtitle: "Medical research, public health",
    icon: "🩺",
    accent: "#FEE2E2",
    accentDark: "#B91C1C",
  },
  {
    value: "COMMUNITY_DEV",
    label: "Community",
    subtitle: "Local services, neighborhoods",
    icon: "🤝",
    accent: "#FEF3C7",
    accentDark: "#B45309",
  },
  {
    value: "AGRICULTURE",
    label: "Agriculture",
    subtitle: "Farming, food systems",
    icon: "🌾",
    accent: "#ECFCCB",
    accentDark: "#4D7C0F",
  },
  {
    value: "RESEARCH",
    label: "Research",
    subtitle: "Science, technology, innovation",
    icon: "🔬",
    accent: "#E0E7FF",
    accentDark: "#4338CA",
  },
  {
    value: "HUMAN_SERVICES",
    label: "Human services",
    subtitle: "Family support, social welfare",
    icon: "💛",
    accent: "#FFF7ED",
    accentDark: "#C2410C",
  },
];

export const getCategoryTile = (value: string | null | undefined): CategoryTile | null => {
  if (!value) {
    return null;
  }
  return CATEGORY_TILES.find((t) => t.value === value) ?? null;
};

export const formatCategoryLabel = (value: string): string => {
  const tile = getCategoryTile(value);
  if (tile) {
    return tile.label;
  }
  return value
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

interface ProgramTopicSources {
  assistanceListings?: string[] | null;
  fundingActivityCategories?: string[] | null;
  causes?: string[] | null;
  category?: string | null;
}

const CAUSE_LABEL_OVERRIDES = new Map<string, string>([
  ["community_dev", "Community"],
  ["womens_issues", "Women's Rights"],
  ["social_services", "Human Services"],
  ["income security and social services", "Human Services"],
  ["employment, labor and training", "Employment"],
  ["law, justice and legal services", "Legal Services"],
  ["food and nutrition", "Food & Nutrition"],
  ["disaster prevention and relief", "Disaster Relief"],
  ["business and commerce", "Business"],
  ["natural resources", "Environment"],
  ["arts", "Arts & Culture"],
  ["humanities", "Arts & Culture"],
  ["science and technology and other research and development", "Science & Research"],
]);

const titleCase = (value: string): string =>
  value
    .toLowerCase()
    .split(/[\s_]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const formatProgramTopicChips = (
  { assistanceListings, fundingActivityCategories, causes, category }: ProgramTopicSources,
  limit = 3
): string[] => {
  const orderedSources: (string[] | null | undefined)[] = [
    fundingActivityCategories,
    causes,
    assistanceListings,
    category ? [category] : null,
  ];

  for (const source of orderedSources) {
    if (!source || source.length === 0) {
      continue;
    }

    const seen = new Set<string>();
    const labels: string[] = [];

    for (const raw of source) {
      if (!raw) {
        continue;
      }
      const trimmed = raw.replace(/^\d+(?:\.\d+)?\s*[-:]\s*/, "").trim();
      const lower = trimmed.toLowerCase();
      if (
        !trimmed ||
        /^\d+(?:\.\d+)?$/.test(trimmed) ||
        lower.includes("other") ||
        lower.includes("see text") ||
        lower === "discretionary"
      ) {
        continue;
      }
      const label =
        CAUSE_LABEL_OVERRIDES.get(lower) ??
        (trimmed === trimmed.toUpperCase() ? titleCase(trimmed) : trimmed.includes("_") ? titleCase(trimmed) : trimmed);
      const key = label.toLowerCase();
      if (seen.has(key) || label.length > 32) {
        continue;
      }
      seen.add(key);
      labels.push(label);
      if (labels.length >= limit) {
        break;
      }
    }

    if (labels.length > 0) {
      return labels;
    }
  }

  return [];
};
